import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { 
  Shield, Users, Car, TrendingUp, AlertTriangle, DollarSign,
  Activity, Eye, MessageSquare, CheckCircle, XCircle, Clock,
  Search, ChevronLeft, ChevronRight, Ban, UserCheck, FileText,
  BarChart3, Settings, LogOut, RefreshCw, Flag, Star, ThumbsUp, 
  ThumbsDown, Trash2, MessageCircle, X, PieChart as PieChartIcon,
  ArrowUpRight, ArrowDownRight, Plus, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';

// Admin role check - case insensitive
const ULTIMATE_ADMINS = [
  "daniel.tawiah@gmail.com",
  "info@iassistpro.com",
  "admin@autotrustgh.com"
];

const isUltimateAdminCheck = (email) => {
  if (!email) return false;
  return ULTIMATE_ADMINS.some(admin => admin.toLowerCase() === email.toLowerCase());
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [trafficData, setTrafficData] = useState(null);
  const [loginData, setLoginData] = useState(null);
  const [fraudData, setFraudData] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0, page: 1 });
  const [listings, setListings] = useState({ listings: [], total: 0, page: 1 });
  const [transactions, setTransactions] = useState({ transactions: [], total: 0, page: 1 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  
  // Reviews state
  const [reviews, setReviews] = useState({ reviews: [], total: 0, page: 1 });
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [moderationReason, setModerationReason] = useState('');
  const [reviewSubTab, setReviewSubTab] = useState('moderation');
  const [reviewAnalytics, setReviewAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Admin listing creation state
  const [showCreateListingModal, setShowCreateListingModal] = useState(false);
  const [newListing, setNewListing] = useState({
    title: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    fuel_type: 'Petrol',
    transmission: 'Automatic',
    condition: 'Used',
    location: 'Accra',
    description: '',
    vin: '',
    seller_id: ''
  });
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [creatingListing, setCreatingListing] = useState(false);

  // Check admin access - case insensitive
  const isAdmin = user && (isUltimateAdminCheck(user.email) || user.admin_role);
  const isUltimateAdmin = user && isUltimateAdminCheck(user.email);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
  }, [user, navigate, isAdmin]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTraffic = async () => {
    try {
      const response = await api.get('/admin/analytics/traffic?days=7');
      setTrafficData(response.data);
    } catch (error) {
      console.error('Failed to fetch traffic data');
    }
  };

  const fetchLogins = async () => {
    try {
      const response = await api.get('/admin/analytics/logins?days=7');
      setLoginData(response.data);
    } catch (error) {
      console.error('Failed to fetch login data');
    }
  };

  const fetchFraud = async () => {
    try {
      const response = await api.get('/admin/fraud/alerts');
      setFraudData(response.data);
    } catch (error) {
      console.error('Failed to fetch fraud data');
    }
  };

  const fetchUsers = async (page = 1, search = '') => {
    try {
      const response = await api.get(`/admin/users?page=${page}&limit=20&search=${search}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchListings = async (page = 1, status = '') => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (status) params.append('status', status);
      const response = await api.get(`/admin/listings?${params}`);
      setListings(response.data);
    } catch (error) {
      console.error('Failed to fetch listings');
    }
  };

  const fetchTransactions = async (page = 1, status = '') => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (status) params.append('status', status);
      const response = await api.get(`/admin/transactions?${params}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions');
    }
  };

  const fetchReviews = async (page = 1, filter = 'all') => {
    try {
      let endpoint = '/admin/reviews';
      const params = new URLSearchParams({ page, limit: 20 });
      
      if (filter === 'pending') {
        endpoint = '/admin/reviews/pending';
      } else if (filter === 'flagged') {
        endpoint = '/admin/reviews/flagged';
      } else if (filter !== 'all') {
        params.append('status', filter);
      }
      
      const response = await api.get(`${endpoint}?${params}`);
      setReviews({
        reviews: response.data.reviews || [],
        total: response.data.total || response.data.count || 0,
        page: response.data.page || page,
        pages: response.data.pages || 1
      });
    } catch (error) {
      console.error('Failed to fetch reviews');
    }
  };

  const fetchReviewStats = async () => {
    try {
      const response = await api.get('/admin/reviews/stats');
      setReviewStats(response.data);
    } catch (error) {
      console.error('Failed to fetch review stats');
    }
  };

  const fetchReviewAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await api.get('/admin/reviews/analytics');
      setReviewAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch review analytics');
      toast.error('Failed to load analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleModerateReview = async (reviewId, action, reason = '') => {
    try {
      await api.put(`/admin/reviews/${reviewId}/moderate`, { action, reason });
      toast.success(`Review ${action}ed successfully`);
      fetchReviews(reviews.page, reviewFilter);
      fetchReviewStats();
      setShowReviewModal(false);
      setModerationReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to moderate review');
    }
  };

  const fetchReviewDetails = async (reviewId) => {
    try {
      const response = await api.get(`/admin/reviews/${reviewId}`);
      setSelectedReview(response.data);
      setShowReviewModal(true);
    } catch (error) {
      toast.error('Failed to fetch review details');
    }
  };

  // Admin listing creation functions
  const searchUsersForListing = async (query) => {
    if (!query || query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    try {
      const response = await api.get(`/admin/users/search?q=${encodeURIComponent(query)}`);
      setUserSearchResults(response.data.users || []);
    } catch (error) {
      console.error('User search failed:', error);
      setUserSearchResults([]);
    }
  };

  const handleCreateListing = async () => {
    if (!newListing.title || !newListing.make || !newListing.model || !newListing.price) {
      toast.error('Please fill in all required fields (Title, Make, Model, Price)');
      return;
    }

    setCreatingListing(true);
    try {
      const response = await api.post('/admin/listings/create', {
        ...newListing,
        price: parseFloat(newListing.price),
        year: parseInt(newListing.year),
        mileage: newListing.mileage ? parseInt(newListing.mileage) : null,
        seller_id: newListing.seller_id || null
      });
      toast.success('Listing created successfully!');
      setShowCreateListingModal(false);
      setNewListing({
        title: '',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        price: '',
        mileage: '',
        fuel_type: 'Petrol',
        transmission: 'Automatic',
        condition: 'Used',
        location: 'Accra',
        description: '',
        vin: '',
        seller_id: ''
      });
      setUserSearchQuery('');
      setUserSearchResults([]);
      // Refresh listings
      fetchListings(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setCreatingListing(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleBanUser = async (userId, ban, reason = '') => {
    try {
      await api.put(`/admin/users/${userId}`, { 
        is_banned: ban, 
        ban_reason: reason 
      });
      toast.success(ban ? 'User banned' : 'User unbanned');
      fetchUsers(users.page, searchQuery);
      if (selectedUser) {
        fetchUserDetails(userId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleAssignRole = async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}`, { role });
      toast.success('Role updated');
      fetchUsers(users.page, searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign role');
    }
  };

  const handleModerateListing = async (listingId, action, reason = '') => {
    try {
      await api.put(`/admin/listings/${listingId}/moderate`, { action, reason });
      toast.success(`Listing ${action}ed`);
      fetchListings(listings.page);
    } catch (error) {
      toast.error('Failed to moderate listing');
    }
  };

  useEffect(() => {
    if (activeTab === 'traffic') fetchTraffic();
    else if (activeTab === 'logins') fetchLogins();
    else if (activeTab === 'fraud') fetchFraud();
    else if (activeTab === 'users') fetchUsers(1, searchQuery);
    else if (activeTab === 'listings') fetchListings(1);
    else if (activeTab === 'transactions') fetchTransactions(1);
    else if (activeTab === 'reviews') {
      fetchReviews(1, reviewFilter);
      fetchReviewStats();
      fetchReviewAnalytics();
    }
  }, [activeTab, reviewFilter]);

  // Fetch analytics when sub-tab changes to analytics
  useEffect(() => {
    if (activeTab === 'reviews' && reviewSubTab === 'analytics' && !reviewAnalytics) {
      fetchReviewAnalytics();
    }
  }, [reviewSubTab, activeTab]);

  // Show loading state while checking auth
  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="text-zinc-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Shield className="w-16 h-16 text-red-500 mx-auto" />
          <h2 className="text-xl font-bold text-white">Access Denied</h2>
          <p className="text-zinc-400">You don't have admin privileges.</p>
          <p className="text-zinc-500 text-sm">Logged in as: {user.email}</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="text-zinc-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'traffic', label: 'Traffic', icon: TrendingUp },
    { id: 'logins', label: 'Login Activity', icon: Activity },
    { id: 'fraud', label: 'Fraud Detection', icon: AlertTriangle },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'listings', label: 'Listings', icon: Car },
    { id: 'transactions', label: 'Transactions', icon: DollarSign },
    { id: 'reviews', label: 'Reviews', icon: Star },
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-500/20 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-zinc-400">
                  {isUltimateAdmin ? 'Ultimate Admin' : dashboardData?.admin_role || 'Admin'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={fetchDashboard}
                className="p-2 text-zinc-400 hover:text-white transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Back to App
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-yellow-500 text-black'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                title="Total Users" 
                value={dashboardData.users.total} 
                subtext={`+${dashboardData.users.new_this_week} this week`}
                icon={Users}
                color="blue"
              />
              <StatCard 
                title="Active Listings" 
                value={dashboardData.listings.active} 
                subtext={`${dashboardData.listings.flagged} flagged`}
                icon={Car}
                color="green"
              />
              <StatCard 
                title="Transactions" 
                value={dashboardData.transactions.total} 
                subtext={`${dashboardData.transactions.pending} pending`}
                icon={DollarSign}
                color="yellow"
              />
              <StatCard 
                title="Revenue" 
                value={`GH₵${dashboardData.transactions.total_revenue.toLocaleString()}`} 
                subtext={`${dashboardData.transactions.completed} completed`}
                icon={TrendingUp}
                color="purple"
              />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4">User Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Verified (KYC)</span>
                    <span className="text-white">{dashboardData.users.verified}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Banned</span>
                    <span className="text-red-400">{dashboardData.users.banned}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">New Today</span>
                    <span className="text-green-400">+{dashboardData.users.new_today}</span>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4">Listing Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Total</span>
                    <span className="text-white">{dashboardData.listings.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Sold</span>
                    <span className="text-green-400">{dashboardData.listings.sold}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Flagged</span>
                    <span className="text-red-400">{dashboardData.listings.flagged}</span>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4">Engagement</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Messages</span>
                    <span className="text-white">{dashboardData.engagement.total_messages}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Conversations</span>
                    <span className="text-white">{dashboardData.engagement.total_conversations}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Disputed</span>
                    <span className="text-orange-400">{dashboardData.transactions.disputed}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Traffic Tab */}
        {activeTab === 'traffic' && trafficData && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Total Views" value={trafficData.total_listing_views} icon={Eye} color="blue" />
              <StatCard title="Active Sessions" value={trafficData.active_sessions} icon={Activity} color="green" />
              <StatCard title="Chatbot Sessions" value={trafficData.chatbot_sessions} icon={MessageSquare} color="purple" />
              <StatCard title="Page Views" value={trafficData.page_views} icon={BarChart3} color="yellow" />
            </div>

            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h3 className="text-lg font-semibold text-white mb-4">Top Performing Listings</h3>
              <div className="space-y-3">
                {trafficData.top_listings.map((listing, idx) => (
                  <div key={listing.listing_id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-500 font-mono">#{idx + 1}</span>
                      <div>
                        <p className="text-white font-medium">{listing.title}</p>
                        <p className="text-yellow-500 text-sm">GH₵{listing.price?.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Eye className="w-4 h-4" />
                      <span>{listing.views} views</span>
                    </div>
                  </div>
                ))}
                {trafficData.top_listings.length === 0 && (
                  <p className="text-zinc-500 text-center py-4">No listings yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Login Activity Tab */}
        {activeTab === 'logins' && loginData && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard title="Successful Logins" value={loginData.successful_logins} icon={CheckCircle} color="green" />
              <StatCard title="Failed Logins" value={loginData.failed_logins} icon={XCircle} color="red" />
              <StatCard title="Suspicious IPs" value={loginData.suspicious_ips.length} icon={AlertTriangle} color="orange" />
            </div>

            {loginData.suspicious_ips.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Suspicious IP Addresses
                </h3>
                <div className="space-y-2">
                  {loginData.suspicious_ips.map((ip, idx) => (
                    <div key={idx} className="flex justify-between p-3 bg-zinc-900 rounded-lg">
                      <span className="text-white font-mono">{ip._id}</span>
                      <span className="text-red-400">{ip.count} failed attempts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Login Activity</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-zinc-500 text-sm border-b border-zinc-800">
                      <th className="pb-3 pr-4">Email</th>
                      <th className="pb-3 pr-4">IP Address</th>
                      <th className="pb-3 pr-4">Status</th>
                      <th className="pb-3">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginData.recent_logins.slice(0, 20).map((login, idx) => (
                      <tr key={idx} className="border-b border-zinc-800/50">
                        <td className="py-3 pr-4 text-white">{login.email}</td>
                        <td className="py-3 pr-4 text-zinc-400 font-mono text-sm">{login.ip_address}</td>
                        <td className="py-3 pr-4">
                          {login.success ? (
                            <span className="text-green-400 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> Success
                            </span>
                          ) : (
                            <span className="text-red-400 flex items-center gap-1">
                              <XCircle className="w-4 h-4" /> Failed
                            </span>
                          )}
                        </td>
                        <td className="py-3 text-zinc-500 text-sm">
                          {new Date(login.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Fraud Detection Tab */}
        {activeTab === 'fraud' && fraudData && (
          <div className="space-y-6">
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-orange-400" />
                <span className="text-orange-400 font-semibold">
                  {fraudData.total_alerts} Active Alerts
                </span>
              </div>
            </div>

            {/* Suspicious Pricing */}
            {fraudData.suspicious_pricing.length > 0 && (
              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-400" />
                  Suspicious Pricing ({fraudData.suspicious_pricing.length})
                </h3>
                <div className="space-y-3">
                  {fraudData.suspicious_pricing.map(listing => (
                    <div key={listing.listing_id} className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{listing.title}</p>
                        <p className="text-sm text-zinc-400">Seller: {listing.seller_name}</p>
                        <p className="text-sm text-red-400">
                          Price: GH₵{listing.price?.toLocaleString()} | 
                          Flagged for review
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleModerateListing(listing.listing_id, 'flag', 'Suspicious pricing')}
                          className="p-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30"
                        >
                          <Flag className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleModerateListing(listing.listing_id, 'delete', 'Fraudulent pricing')}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Duplicate VINs */}
            {fraudData.duplicate_vins.length > 0 && (
              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Car className="w-5 h-5 text-yellow-400" />
                  Duplicate VINs ({fraudData.duplicate_vins.length})
                </h3>
                <div className="space-y-3">
                  {fraudData.duplicate_vins.map((dup, idx) => (
                    <div key={idx} className="p-4 bg-zinc-800/50 rounded-lg">
                      <p className="text-yellow-400 font-mono mb-2">VIN: {dup._id}</p>
                      <p className="text-sm text-zinc-400 mb-2">{dup.count} listings with this VIN:</p>
                      <div className="space-y-1">
                        {dup.listings.map(l => (
                          <p key={l.listing_id} className="text-sm text-white">
                            • {l.title} (Seller: {l.seller_id})
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Disputed Transactions */}
            {fraudData.disputed_transactions.length > 0 && (
              <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Disputed Transactions ({fraudData.disputed_transactions.length})
                </h3>
                <div className="space-y-3">
                  {fraudData.disputed_transactions.map(tx => (
                    <div key={tx.transaction_id} className="p-4 bg-zinc-800/50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">Transaction: {tx.transaction_id}</p>
                          <p className="text-yellow-500">GH₵{tx.amount?.toLocaleString()}</p>
                          <p className="text-sm text-zinc-400 mt-1">
                            Buyer: {tx.buyer_id} | Seller: {tx.seller_id}
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                          Disputed
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {fraudData.total_alerts === 0 && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-green-400 font-semibold">No fraud alerts detected</p>
                <p className="text-zinc-400 text-sm mt-1">All systems operating normally</p>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search by email, name, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchUsers(1, searchQuery)}
                  className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                />
              </div>
              <button
                onClick={() => fetchUsers(1, searchQuery)}
                className="px-6 py-3 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400"
              >
                Search
              </button>
            </div>

            {/* Users Table */}
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-zinc-500 text-sm bg-zinc-800/50">
                      <th className="p-4">User</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Role</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Listings</th>
                      <th className="p-4">Joined</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.users.map(u => (
                      <tr key={u.user_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <span className="text-white font-semibold">{u.name?.[0]?.toUpperCase() || '?'}</span>
                              )}
                            </div>
                            <span className="text-white font-medium">{u.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="p-4 text-zinc-400">{u.email}</td>
                        <td className="p-4">
                          {u.is_ultimate_admin ? (
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">Ultimate Admin</span>
                          ) : u.admin_role ? (
                            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">{u.admin_role}</span>
                          ) : u.kyc_verified ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Verified</span>
                          ) : (
                            <span className="px-2 py-1 bg-zinc-700 text-zinc-400 rounded text-xs">User</span>
                          )}
                        </td>
                        <td className="p-4">
                          {u.is_banned ? (
                            <span className="text-red-400 flex items-center gap-1">
                              <Ban className="w-4 h-4" /> Banned
                            </span>
                          ) : (
                            <span className="text-green-400">Active</span>
                          )}
                        </td>
                        <td className="p-4 text-zinc-400">{u.listing_count}</td>
                        <td className="p-4 text-zinc-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => fetchUserDetails(u.user_id)}
                              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {!u.is_ultimate_admin && (
                              <>
                                {u.is_banned ? (
                                  <button
                                    onClick={() => handleBanUser(u.user_id, false)}
                                    className="p-2 text-green-400 hover:bg-green-500/20 rounded"
                                    title="Unban User"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      const reason = prompt('Ban reason:');
                                      if (reason) handleBanUser(u.user_id, true, reason);
                                    }}
                                    className="p-2 text-red-400 hover:bg-red-500/20 rounded"
                                    title="Ban User"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                <span className="text-zinc-500 text-sm">
                  Showing {users.users.length} of {users.total} users
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchUsers(users.page - 1, searchQuery)}
                    disabled={users.page <= 1}
                    className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-4 py-2 text-white">
                    Page {users.page} of {users.pages}
                  </span>
                  <button
                    onClick={() => fetchUsers(users.page + 1, searchQuery)}
                    disabled={users.page >= users.pages}
                    className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div className="space-y-6">
            {/* Admin Create Listing Button - Ultimate Admins Only */}
            {isUltimateAdmin && (
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCreateListingModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors font-medium"
                  data-testid="admin-create-listing-btn"
                >
                  <Plus className="w-5 h-5" />
                  Create Listing (No Payment)
                </button>
              </div>
            )}

            <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-zinc-500 text-sm bg-zinc-800/50">
                      <th className="p-4">Listing</th>
                      <th className="p-4">Price</th>
                      <th className="p-4">Seller</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Trust Score</th>
                      <th className="p-4">Created</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.listings.map(l => (
                      <tr key={l.listing_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{l.title}</p>
                            <p className="text-sm text-zinc-500">{l.make} {l.model} {l.year}</p>
                          </div>
                        </td>
                        <td className="p-4 text-yellow-500 font-semibold">
                          GH₵{l.price?.toLocaleString()}
                        </td>
                        <td className="p-4 text-zinc-400">{l.seller_name || l.seller_id}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            l.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            l.status === 'sold' ? 'bg-blue-500/20 text-blue-400' :
                            l.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-700 text-zinc-400'
                          }`}>
                            {l.status}
                          </span>
                          {l.is_flagged && (
                            <span className="ml-2 px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs">
                              Flagged
                            </span>
                          )}
                          {l.is_featured && (
                            <span className="ml-2 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                              Featured
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`${l.trust_score >= 80 ? 'text-green-400' : l.trust_score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {l.trust_score}/100
                          </span>
                        </td>
                        <td className="p-4 text-zinc-500 text-sm">
                          {new Date(l.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            <button
                              onClick={() => window.open(`/listing/${l.listing_id}`, '_blank')}
                              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {!l.is_featured && (
                              <button
                                onClick={() => handleModerateListing(l.listing_id, 'feature')}
                                className="p-2 text-yellow-400 hover:bg-yellow-500/20 rounded"
                                title="Feature"
                              >
                                <Star className="w-4 h-4" />
                              </button>
                            )}
                            {!l.is_flagged ? (
                              <button
                                onClick={() => {
                                  const reason = prompt('Flag reason:');
                                  if (reason) handleModerateListing(l.listing_id, 'flag', reason);
                                }}
                                className="p-2 text-orange-400 hover:bg-orange-500/20 rounded"
                                title="Flag"
                              >
                                <Flag className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleModerateListing(l.listing_id, 'unflag')}
                                className="p-2 text-green-400 hover:bg-green-500/20 rounded"
                                title="Unflag"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                if (confirm('Delete this listing?')) handleModerateListing(l.listing_id, 'delete');
                              }}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded"
                              title="Delete"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                <span className="text-zinc-500 text-sm">
                  Showing {listings.listings.length} of {listings.total} listings
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchListings(listings.page - 1)}
                    disabled={listings.page <= 1}
                    className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-4 py-2 text-white">
                    Page {listings.page} of {listings.pages}
                  </span>
                  <button
                    onClick={() => fetchListings(listings.page + 1)}
                    disabled={listings.page >= listings.pages}
                    className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="space-y-6">
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-zinc-500 text-sm bg-zinc-800/50">
                      <th className="p-4">Transaction ID</th>
                      <th className="p-4">Amount</th>
                      <th className="p-4">Buyer</th>
                      <th className="p-4">Seller</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.transactions.map(tx => (
                      <tr key={tx.transaction_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-4 text-white font-mono text-sm">{tx.transaction_id}</td>
                        <td className="p-4 text-yellow-500 font-semibold">
                          GH₵{tx.amount?.toLocaleString()}
                        </td>
                        <td className="p-4 text-zinc-400">{tx.buyer_id}</td>
                        <td className="p-4 text-zinc-400">{tx.seller_id}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            tx.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            tx.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            tx.status === 'disputed' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-700 text-zinc-400'
                          }`}>
                            {tx.status}
                          </span>
                        </td>
                        <td className="p-4 text-zinc-500 text-sm">
                          {new Date(tx.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {transactions.transactions.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-zinc-500">
                          No transactions yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                <span className="text-zinc-500 text-sm">
                  Showing {transactions.transactions.length} of {transactions.total} transactions
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchTransactions(transactions.page - 1)}
                    disabled={transactions.page <= 1}
                    className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-4 py-2 text-white">
                    Page {transactions.page} of {transactions.pages}
                  </span>
                  <button
                    onClick={() => fetchTransactions(transactions.page + 1)}
                    disabled={transactions.page >= transactions.pages}
                    className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {/* Sub-tabs for Reviews */}
            <div className="flex gap-4 border-b border-zinc-800 pb-4">
              <button
                onClick={() => setReviewSubTab('analytics')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  reviewSubTab === 'analytics'
                    ? 'bg-yellow-500 text-black'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
                data-testid="review-analytics-tab"
              >
                <PieChartIcon className="w-4 h-4" />
                Analytics
              </button>
              <button
                onClick={() => setReviewSubTab('moderation')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  reviewSubTab === 'moderation'
                    ? 'bg-yellow-500 text-black'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
                data-testid="review-moderation-tab"
              >
                <Star className="w-4 h-4" />
                Moderation
              </button>
            </div>

            {/* Analytics Sub-tab */}
            {reviewSubTab === 'analytics' && (
              <div className="space-y-6">
                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
                  </div>
                ) : reviewAnalytics ? (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800" data-testid="total-reviews-card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Total Reviews</p>
                            <p className="text-3xl font-bold text-white mt-1">{reviewAnalytics.summary.total_reviews}</p>
                          </div>
                          <div className="p-3 bg-blue-500/20 rounded-lg">
                            <MessageCircle className="w-6 h-6 text-blue-400" />
                          </div>
                        </div>
                      </div>
                      <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800" data-testid="avg-rating-card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Average Rating</p>
                            <p className="text-3xl font-bold text-white mt-1 flex items-center gap-2">
                              {reviewAnalytics.summary.average_rating}
                              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                            </p>
                          </div>
                          <div className="p-3 bg-yellow-500/20 rounded-lg">
                            <Star className="w-6 h-6 text-yellow-400" />
                          </div>
                        </div>
                      </div>
                      <div className="bg-zinc-900 rounded-xl p-5 border border-yellow-500/30" data-testid="pending-count-card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Pending Review</p>
                            <p className="text-3xl font-bold text-yellow-400 mt-1">{reviewAnalytics.summary.pending_count}</p>
                          </div>
                          <div className="p-3 bg-yellow-500/20 rounded-lg">
                            <Clock className="w-6 h-6 text-yellow-400" />
                          </div>
                        </div>
                      </div>
                      <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800" data-testid="week-reviews-card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">This Week</p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-3xl font-bold text-white">{reviewAnalytics.summary.this_week}</p>
                              {reviewAnalytics.summary.week_change_percent !== 0 && (
                                <span className={`flex items-center text-sm font-medium ${
                                  reviewAnalytics.summary.week_change_percent > 0 ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {reviewAnalytics.summary.week_change_percent > 0 ? (
                                    <ArrowUpRight className="w-4 h-4" />
                                  ) : (
                                    <ArrowDownRight className="w-4 h-4" />
                                  )}
                                  {Math.abs(reviewAnalytics.summary.week_change_percent)}%
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="p-3 bg-green-500/20 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-green-400" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Status Distribution Pie Chart */}
                      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800" data-testid="status-pie-chart">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                          <PieChartIcon className="w-5 h-5 text-yellow-400" />
                          Review Status Distribution
                        </h3>
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={reviewAnalytics.status_distribution.filter(d => d.value > 0)}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={90}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, value, percent }) => `${name}: ${value}`}
                                labelLine={{ stroke: '#71717a', strokeWidth: 1 }}
                              >
                                {reviewAnalytics.status_distribution.filter(d => d.value > 0).map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#18181b', 
                                  border: '1px solid #3f3f46',
                                  borderRadius: '8px',
                                  color: '#fff'
                                }}
                              />
                              <Legend 
                                verticalAlign="bottom" 
                                height={36}
                                formatter={(value) => <span style={{ color: '#a1a1aa' }}>{value}</span>}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Rating Distribution Bar Chart */}
                      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800" data-testid="rating-bar-chart">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-yellow-400" />
                          Rating Distribution
                        </h3>
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={reviewAnalytics.rating_distribution} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                              <XAxis type="number" stroke="#71717a" />
                              <YAxis 
                                dataKey="rating" 
                                type="category" 
                                stroke="#71717a" 
                                width={80}
                                tick={{ fill: '#a1a1aa', fontSize: 12 }}
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#18181b', 
                                  border: '1px solid #3f3f46',
                                  borderRadius: '8px',
                                  color: '#fff'
                                }}
                                formatter={(value) => [`${value} reviews`, 'Count']}
                              />
                              <Bar 
                                dataKey="count" 
                                fill="#EAB308" 
                                radius={[0, 4, 4, 0]}
                                maxBarSize={30}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    {/* Reviews Over Time */}
                    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800" data-testid="reviews-over-time-chart">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-yellow-400" />
                        Reviews Over Time (Last 30 Days)
                      </h3>
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={reviewAnalytics.reviews_over_time}>
                            <defs>
                              <linearGradient id="colorReviews" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#EAB308" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#EAB308" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                            <XAxis 
                              dataKey="day" 
                              stroke="#71717a" 
                              tick={{ fill: '#71717a', fontSize: 10 }}
                              interval={4}
                            />
                            <YAxis stroke="#71717a" tick={{ fill: '#71717a' }} />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: '#18181b', 
                                border: '1px solid #3f3f46',
                                borderRadius: '8px',
                                color: '#fff'
                              }}
                              formatter={(value) => [`${value} reviews`, 'Count']}
                              labelFormatter={(label) => `Date: ${label}`}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="count" 
                              stroke="#EAB308" 
                              strokeWidth={2}
                              fillOpacity={1} 
                              fill="url(#colorReviews)" 
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Top Reviewed Sellers */}
                    {reviewAnalytics.top_reviewed_sellers && reviewAnalytics.top_reviewed_sellers.length > 0 && (
                      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800" data-testid="top-reviewed-sellers">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                          <Users className="w-5 h-5 text-yellow-400" />
                          Top Reviewed Sellers
                        </h3>
                        <div className="space-y-3">
                          {reviewAnalytics.top_reviewed_sellers.map((seller, idx) => (
                            <div 
                              key={seller.user_id} 
                              className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center text-yellow-400 font-bold">
                                  #{idx + 1}
                                </div>
                                <div>
                                  <p className="text-white font-medium">{seller.name}</p>
                                  <p className="text-zinc-500 text-sm">{seller.review_count} reviews</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <Star 
                                      key={star}
                                      className={`w-4 h-4 ${star <= Math.round(seller.avg_rating) ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`}
                                    />
                                  ))}
                                </div>
                                <span className="text-white font-medium ml-2">{seller.avg_rating}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <PieChartIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No analytics data available</p>
                    <button 
                      onClick={fetchReviewAnalytics}
                      className="mt-4 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors"
                    >
                      Load Analytics
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Moderation Sub-tab */}
            {reviewSubTab === 'moderation' && (
              <>
                {/* Review Stats */}
                {reviewStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
                      <p className="text-zinc-500 text-xs">Total Reviews</p>
                      <p className="text-2xl font-bold text-white">{reviewStats.total}</p>
                    </div>
                    <div className="bg-zinc-900 rounded-xl p-4 border border-yellow-500/30">
                      <p className="text-zinc-500 text-xs">Pending</p>
                      <p className="text-2xl font-bold text-yellow-400">{reviewStats.pending}</p>
                    </div>
                    <div className="bg-zinc-900 rounded-xl p-4 border border-green-500/30">
                      <p className="text-zinc-500 text-xs">Approved</p>
                      <p className="text-2xl font-bold text-green-400">{reviewStats.approved}</p>
                    </div>
                    <div className="bg-zinc-900 rounded-xl p-4 border border-red-500/30">
                      <p className="text-zinc-500 text-xs">Flagged</p>
                      <p className="text-2xl font-bold text-red-400">{reviewStats.flagged}</p>
                    </div>
                    <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
                      <p className="text-zinc-500 text-xs">Avg Rating</p>
                      <p className="text-2xl font-bold text-white flex items-center gap-1">
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                        {reviewStats.average_rating}
                      </p>
                    </div>
                    <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
                      <p className="text-zinc-500 text-xs">This Week</p>
                      <p className="text-2xl font-bold text-white">{reviewStats.reviews_this_week}</p>
                    </div>
                  </div>
                )}

                {/* Filter Tabs */}
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'all', label: 'All Reviews' },
                    { id: 'pending', label: 'Pending', color: 'yellow' },
                    { id: 'flagged', label: 'Flagged', color: 'red' },
                    { id: 'approved', label: 'Approved', color: 'green' },
                    { id: 'rejected', label: 'Rejected', color: 'gray' },
                  ].map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => setReviewFilter(filter.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        reviewFilter === filter.id
                          ? filter.color === 'yellow' ? 'bg-yellow-500 text-black'
                          : filter.color === 'red' ? 'bg-red-500 text-white'
                          : filter.color === 'green' ? 'bg-green-500 text-white'
                          : 'bg-zinc-700 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:text-white'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                {/* Reviews List */}
                <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-zinc-800/50">
                        <tr>
                          <th className="text-left p-4 text-zinc-400 text-sm font-medium">Reviewer</th>
                          <th className="text-left p-4 text-zinc-400 text-sm font-medium">Rating</th>
                          <th className="text-left p-4 text-zinc-400 text-sm font-medium">Comment</th>
                          <th className="text-left p-4 text-zinc-400 text-sm font-medium">Status</th>
                          <th className="text-left p-4 text-zinc-400 text-sm font-medium">Date</th>
                          <th className="text-right p-4 text-zinc-400 text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800">
                        {reviews.reviews.map(review => (
                          <tr key={review.review_id} className="hover:bg-zinc-800/30">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-white text-sm font-medium">
                                  {review.reviewer_info?.name?.[0] || 'U'}
                                </div>
                                <div>
                                  <p className="text-white font-medium text-sm">{review.reviewer_info?.name || 'Unknown'}</p>
                                  <p className="text-zinc-500 text-xs">{review.reviewer_info?.email || ''}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map(star => (
                                  <Star 
                                    key={star} 
                                    className={`w-4 h-4 ${star <= (review.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} 
                                  />
                                ))}
                              </div>
                            </td>
                            <td className="p-4 max-w-xs">
                              <p className="text-zinc-300 text-sm truncate">{review.comment || review.content || '-'}</p>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                review.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                review.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                review.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                review.status === 'deleted' ? 'bg-zinc-500/20 text-zinc-400' :
                                'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {review.status || 'pending'}
                              </span>
                              {review.is_flagged && (
                                <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                                  <Flag className="w-3 h-3 inline mr-1" />
                                  Flagged
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-zinc-400 text-sm">
                              {new Date(review.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => fetchReviewDetails(review.review_id)}
                                  className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                {review.status !== 'approved' && (
                                  <button
                                    onClick={() => handleModerateReview(review.review_id, 'approve')}
                                    className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors"
                                    title="Approve"
                                  >
                                    <ThumbsUp className="w-4 h-4" />
                                  </button>
                                )}
                                {review.status !== 'rejected' && (
                                  <button
                                    onClick={() => {
                                      setSelectedReview(review);
                                      setShowReviewModal(true);
                                    }}
                                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                                    title="Reject"
                                  >
                                    <ThumbsDown className="w-4 h-4" />
                                  </button>
                                )}
                                {!review.is_flagged ? (
                                  <button
                                    onClick={() => handleModerateReview(review.review_id, 'flag', 'Flagged for review')}
                                    className="p-2 text-yellow-400 hover:bg-yellow-500/20 rounded-lg transition-colors"
                                    title="Flag"
                                  >
                                    <Flag className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleModerateReview(review.review_id, 'unflag')}
                                    className="p-2 text-zinc-400 hover:bg-zinc-700 rounded-lg transition-colors"
                                    title="Unflag"
                                  >
                                    <Flag className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {reviews.reviews.length === 0 && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-zinc-500">
                              No reviews found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {reviews.total > 20 && (
                    <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
                      <p className="text-sm text-zinc-500">
                        Showing {((reviews.page - 1) * 20) + 1} - {Math.min(reviews.page * 20, reviews.total)} of {reviews.total}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchReviews(reviews.page - 1, reviewFilter)}
                          disabled={reviews.page <= 1}
                          className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => fetchReviews(reviews.page + 1, reviewFilter)}
                          disabled={reviews.page >= reviews.pages}
                          className="p-2 bg-zinc-800 text-zinc-400 rounded disabled:opacity-50"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <UserDetailModal 
          user={selectedUser}
          isUltimateAdmin={isUltimateAdmin}
          onClose={() => setShowUserModal(false)}
          onBan={handleBanUser}
          onAssignRole={handleAssignRole}
        />
      )}

      {/* Review Detail Modal */}
      {showReviewModal && selectedReview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Review Details</h2>
              <button
                onClick={() => { setShowReviewModal(false); setSelectedReview(null); setModerationReason(''); }}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Review Content */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 mb-2">Review</h3>
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star 
                        key={star} 
                        className={`w-5 h-5 ${star <= (selectedReview.review?.rating || selectedReview.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} 
                      />
                    ))}
                    <span className="text-white ml-2 font-semibold">
                      {selectedReview.review?.rating || selectedReview.rating || 0}/5
                    </span>
                  </div>
                  <p className="text-white">
                    {selectedReview.review?.comment || selectedReview.comment || selectedReview.review?.content || selectedReview.content || 'No comment provided'}
                  </p>
                  <p className="text-zinc-500 text-sm mt-2">
                    Posted: {new Date(selectedReview.review?.created_at || selectedReview.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Reviewer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Reviewer</h3>
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <p className="text-white font-medium">{selectedReview.reviewer?.name || selectedReview.reviewer_info?.name || 'Unknown'}</p>
                    <p className="text-zinc-400 text-sm">{selectedReview.reviewer?.email || selectedReview.reviewer_info?.email || ''}</p>
                    {selectedReview.reviewer?.kyc_verified && (
                      <span className="inline-flex items-center gap-1 mt-2 text-xs text-green-400">
                        <CheckCircle className="w-3 h-3" /> KYC Verified
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Reviewed User</h3>
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <p className="text-white font-medium">{selectedReview.reviewed_user?.name || selectedReview.reviewed_user_info?.name || 'Unknown'}</p>
                    <p className="text-zinc-400 text-sm">{selectedReview.reviewed_user?.email || selectedReview.reviewed_user_info?.email || ''}</p>
                  </div>
                </div>
              </div>

              {/* Related Listing */}
              {selectedReview.listing && (
                <div>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Related Listing</h3>
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <p className="text-white font-medium">{selectedReview.listing.title}</p>
                    <p className="text-yellow-400">GH₵{selectedReview.listing.price?.toLocaleString()}</p>
                  </div>
                </div>
              )}

              {/* Moderation Actions */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 mb-2">Moderation</h3>
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
                  <input
                    type="text"
                    placeholder="Reason for action (optional)..."
                    value={moderationReason}
                    onChange={(e) => setModerationReason(e.target.value)}
                    className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleModerateReview(selectedReview.review?.review_id || selectedReview.review_id, 'approve')}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <ThumbsUp className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleModerateReview(selectedReview.review?.review_id || selectedReview.review_id, 'reject', moderationReason)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <ThumbsDown className="w-4 h-4" /> Reject
                    </button>
                    <button
                      onClick={() => handleModerateReview(selectedReview.review?.review_id || selectedReview.review_id, 'flag', moderationReason || 'Flagged for review')}
                      className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <Flag className="w-4 h-4" /> Flag
                    </button>
                    <button
                      onClick={() => handleModerateReview(selectedReview.review?.review_id || selectedReview.review_id, 'delete', moderationReason)}
                      className="px-4 py-2 bg-zinc-600 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Listing Modal - Ultimate Admins Only */}
      {showCreateListingModal && isUltimateAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Plus className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Create Listing</h2>
                  <p className="text-xs text-zinc-500">Ultimate Admin - No Payment Required</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateListingModal(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Title *</label>
                <input
                  type="text"
                  value={newListing.title}
                  onChange={(e) => setNewListing({ ...newListing, title: e.target.value })}
                  placeholder="e.g., 2020 Toyota Camry SE"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                  data-testid="admin-listing-title"
                />
              </div>

              {/* Make, Model, Year */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Make *</label>
                  <input
                    type="text"
                    value={newListing.make}
                    onChange={(e) => setNewListing({ ...newListing, make: e.target.value })}
                    placeholder="Toyota"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Model *</label>
                  <input
                    type="text"
                    value={newListing.model}
                    onChange={(e) => setNewListing({ ...newListing, model: e.target.value })}
                    placeholder="Camry"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Year *</label>
                  <input
                    type="number"
                    value={newListing.year}
                    onChange={(e) => setNewListing({ ...newListing, year: e.target.value })}
                    min="1990"
                    max="2026"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
              </div>

              {/* Price and Mileage */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Price (GH₵) *</label>
                  <input
                    type="number"
                    value={newListing.price}
                    onChange={(e) => setNewListing({ ...newListing, price: e.target.value })}
                    placeholder="150000"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Mileage (km)</label>
                  <input
                    type="number"
                    value={newListing.mileage}
                    onChange={(e) => setNewListing({ ...newListing, mileage: e.target.value })}
                    placeholder="50000"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                  />
                </div>
              </div>

              {/* Fuel Type, Transmission, Condition */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Fuel Type</label>
                  <select
                    value={newListing.fuel_type}
                    onChange={(e) => setNewListing({ ...newListing, fuel_type: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  >
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Transmission</label>
                  <select
                    value={newListing.transmission}
                    onChange={(e) => setNewListing({ ...newListing, transmission: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  >
                    <option value="Automatic">Automatic</option>
                    <option value="Manual">Manual</option>
                    <option value="CVT">CVT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Condition</label>
                  <select
                    value={newListing.condition}
                    onChange={(e) => setNewListing({ ...newListing, condition: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  >
                    <option value="New">New</option>
                    <option value="Used">Used</option>
                    <option value="Certified Pre-Owned">Certified Pre-Owned</option>
                  </select>
                </div>
              </div>

              {/* Location and VIN */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Location</label>
                  <select
                    value={newListing.location}
                    onChange={(e) => setNewListing({ ...newListing, location: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  >
                    <option value="Accra">Accra</option>
                    <option value="Kumasi">Kumasi</option>
                    <option value="Tamale">Tamale</option>
                    <option value="Tema">Tema</option>
                    <option value="Cape Coast">Cape Coast</option>
                    <option value="Takoradi">Takoradi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">VIN (Optional)</label>
                  <input
                    type="text"
                    value={newListing.vin}
                    onChange={(e) => setNewListing({ ...newListing, vin: e.target.value.toUpperCase() })}
                    placeholder="17-character VIN"
                    maxLength={17}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500 font-mono"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    {newListing.vin ? `Trust Score: ${newListing.vin.length === 17 ? '95' : '70'}` : 'No VIN = Trust Score 70'}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description</label>
                <textarea
                  value={newListing.description}
                  onChange={(e) => setNewListing({ ...newListing, description: e.target.value })}
                  placeholder="Enter vehicle description..."
                  rows={3}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500 resize-none"
                />
              </div>

              {/* Assign to User */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Assign to User (Optional)
                </label>
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => {
                    setUserSearchQuery(e.target.value);
                    searchUsersForListing(e.target.value);
                  }}
                  placeholder="Search by name or email..."
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-500"
                />
                {userSearchResults.length > 0 && (
                  <div className="mt-2 bg-zinc-800 border border-zinc-700 rounded-lg max-h-40 overflow-y-auto">
                    {userSearchResults.map(u => (
                      <button
                        key={u.user_id}
                        onClick={() => {
                          setNewListing({ ...newListing, seller_id: u.user_id });
                          setUserSearchQuery(`${u.name} (${u.email})`);
                          setUserSearchResults([]);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-zinc-700 text-white flex justify-between items-center"
                      >
                        <span>{u.name}</span>
                        <span className="text-zinc-500 text-sm">{u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {newListing.seller_id && (
                  <p className="text-xs text-green-400 mt-1">
                    ✓ Listing will be assigned to: {newListing.seller_id}
                  </p>
                )}
                {!newListing.seller_id && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Leave empty to assign to yourself
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-zinc-800">
                <button
                  onClick={() => setShowCreateListingModal(false)}
                  className="flex-1 px-4 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateListing}
                  disabled={creatingListing}
                  className="flex-1 px-4 py-3 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="admin-submit-listing"
                >
                  {creatingListing ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      Create Listing
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, subtext, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    purple: 'bg-purple-500/20 text-purple-400',
    red: 'bg-red-500/20 text-red-400',
    orange: 'bg-orange-500/20 text-orange-400'
  };

  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-500 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subtext && <p className="text-xs text-zinc-500 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

// User Detail Modal Component
const UserDetailModal = ({ user, isUltimateAdmin, onClose, onBan, onAssignRole }) => {
  const [note, setNote] = useState('');
  const [selectedRole, setSelectedRole] = useState(user.user?.admin_role || '');

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/users/${user.user.user_id}/notes`, { note });
      toast.success('Note added');
      setNote('');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">User Details</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-zinc-700 rounded-full flex items-center justify-center">
              {user.user?.picture ? (
                <img src={user.user.picture} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-3xl text-white">{user.user?.name?.[0]?.toUpperCase() || '?'}</span>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white">{user.user?.name || 'Unknown'}</h3>
              <p className="text-zinc-400">{user.user?.email}</p>
              <p className="text-zinc-500 text-sm">{user.user?.phone}</p>
              <div className="flex gap-2 mt-2">
                {user.user?.is_ultimate_admin && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">Ultimate Admin</span>
                )}
                {user.user?.kyc_verified && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">KYC Verified</span>
                )}
                {user.user?.is_banned && (
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">Banned</span>
                )}
              </div>
            </div>

            {/* Actions */}
            {!user.user?.is_ultimate_admin && (
              <div className="space-y-2">
                {user.user?.is_banned ? (
                  <button
                    onClick={() => onBan(user.user.user_id, false)}
                    className="w-full px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                  >
                    Unban User
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const reason = prompt('Ban reason:');
                      if (reason) onBan(user.user.user_id, true, reason);
                    }}
                    className="w-full px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                  >
                    Ban User
                  </button>
                )}

                {isUltimateAdmin && (
                  <div className="flex gap-2">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                    >
                      <option value="">No admin role</option>
                      <option value="viewer">Viewer</option>
                      <option value="support">Support</option>
                      <option value="moderator">Moderator</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      onClick={() => onAssignRole(user.user.user_id, selectedRole)}
                      className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 text-sm"
                    >
                      Assign
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Listings */}
          {user.listings?.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-3">Listings ({user.listings.length})</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {user.listings.map(l => (
                  <div key={l.listing_id} className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                    <div>
                      <p className="text-white">{l.title}</p>
                      <p className="text-yellow-500 text-sm">GH₵{l.price?.toLocaleString()}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      l.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {l.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Notes */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-3">Admin Notes</h4>
            <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
              {user.admin_notes?.map(n => (
                <div key={n.note_id} className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-white">{n.note}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    By {n.admin_name} • {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
              {(!user.admin_notes || user.admin_notes.length === 0) && (
                <p className="text-zinc-500 text-sm">No admin notes</p>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add a note..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="flex-1 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500"
              />
              <button
                onClick={addNote}
                className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400"
              >
                Add Note
              </button>
            </div>
          </div>

          {/* Login History */}
          {user.login_history?.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-3">Login History</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {user.login_history.map((login, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg text-sm">
                    <span className="text-zinc-400 font-mono">{login.ip_address}</span>
                    <span className={login.success ? 'text-green-400' : 'text-red-400'}>
                      {login.success ? 'Success' : 'Failed'}
                    </span>
                    <span className="text-zinc-500">{new Date(login.timestamp).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
